import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useLogo } from '@/hooks/useLogo';
import { ProfileList, type Profile } from './ProfileList';
import { clearGroupsCache } from '@/hooks/useGroupChildren';
import { clearUserInfoCache } from '@/hooks/useUserInfo';
import { clearUnitsCache } from '@/lib/unitsCache';
import { clearRolesCache } from '@/lib/rolesCache';
import { clearDigitalAssetsCache } from '@/lib/digitalAssetsCache';
import { clearMessageBoardCache } from '@/lib/messageBoardCache';
import { useQueryClient } from '@tanstack/react-query';

interface ProfileSelectorProps {
  profiles: Profile[];
  backgroundImage: string;
  onProfileSelected?: () => void;
}

export const ProfileSelector = ({ profiles, backgroundImage, onProfileSelected }: ProfileSelectorProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const filteredProfiles = profiles;

  const handleSelectProfile = (profile: Profile) => {
    clearGroupsCache();
    clearUserInfoCache();
    clearUnitsCache();
    clearRolesCache();
    clearDigitalAssetsCache();
    clearMessageBoardCache();
    queryClient.invalidateQueries({ queryKey: ['get-favorites-lists'] });
    queryClient.invalidateQueries({ queryKey: ['get-favorites-list-detail'] });
    queryClient.invalidateQueries({ queryKey: ['favorites-all-items'] });
    sessionStorage.removeItem('userProfiles');
    sessionStorage.setItem('selectedProfile', JSON.stringify({
      customerNumber: profile.customerNumber,
      company: profile.company,
      id: profile.id,
      address: profile.address,
      address2: profile.address2,
      city: profile.city,
      state: profile.state,
      zip: profile.zip,
      country: profile.country,
      countryCode: profile.countryCode,
    }));
    onProfileSelected?.();
  };

  const handleBypass = () => {
    onProfileSelected?.();
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      <div className="relative z-10 w-full max-w-2xl">
        <div className="absolute inset-0 bg-white/10 backdrop-blur-sm rounded-lg -z-10" />
        <Card className="w-full bg-transparent text-white border-none shadow-2xl backdrop-blur-sm rounded-lg">
          <CardContent className="pt-6">
            {filteredProfiles.length === 0 ? (
              <div className="text-center space-y-4 py-4">
                <AlertCircle className="h-12 w-12 text-yellow-300 mx-auto" />
                <h2 className="text-xl font-bold text-white">No Profiles configured</h2>
                <Button
                  onClick={handleBypass}
                  className="w-full bg-white text-[#0066cc] hover:bg-white/90 rounded-md"
                >
                  Take me to the homepage nonetheless - we'll fix it later
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <h2 className="text-lg font-semibold text-white text-center mb-4">Select a Profile</h2>
                <ProfileList
                  profiles={filteredProfiles}
                  onSelect={handleSelectProfile}
                  variant="dark"
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
